import React, { useState, useCallback } from 'react';
import { View, StyleSheet, TouchableOpacity, StatusBar, ScrollView } from 'react-native';
import { Text } from '@/components/common/text';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Icon from 'react-native-vector-icons/Ionicons';
import { useDeleteAccountMutation } from '@/store/api';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { strings } from '@/constants/residents/strings';
import { useAppDispatch } from '@/store';
import { logout } from '@/store/slices/authSlice';
import { showAlert, showConfirm } from '@/utils/common/alert';
import { GradientBackground, GlassCard } from '@/components/residents/redesign';
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { BackLink } from '@/components/residents/mobile/BackLink';

const HP = REDESIGN_SPACING.horizontalPadding;

export const DeleteAccountScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation<any>();
  const insets = useSafeAreaInsets();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteAccount] = useDeleteAccountMutation();

  const handleBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const confirmAndSubmit = useCallback(async () => {
    const confirmed = await showConfirm(
      strings.alert.deleteAccountTitle,
      strings.alert.deleteAccountMessage,
      strings.alert.deleteAccountAction,
      strings.alert.cancel
    );

    if (!confirmed) return;

    setIsSubmitting(true);
    try {
      const resp = await deleteAccount(undefined as any).unwrap();
      if ((resp as any)?.success) {
        navigation.navigate('DeleteAccountConfirmed');
      } else {
        showAlert(strings.alert.error, 'Failed to delete account');
      }
    } catch (e) {
      showAlert(strings.alert.error, 'Failed to delete account');
    } finally {
      setIsSubmitting(false);
    }
  }, [deleteAccount, navigation]);

  return (
    <GradientBackground>
      <StatusBar barStyle="light-content" />
      <BaseResidentMobileScreen
        screenName="DeleteAccount"
        darkBackground
        headerVariant="gradient"
      >
        <View style={[styles.container, { paddingBottom: insets.bottom + 16 }]}>
          <ScrollView
            style={styles.flex}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            <BackLink
              label="Back to settings"
              onPress={handleBack}
              color={PRIMITIVE_COLORS.resioBlue}
            />

            <GlassCard borderRadius={20} padding={24} style={styles.warningCard}>
              <View style={[styles.warningIconCircle, { backgroundColor: colors.accent.redBg }]}>
                <Icon name="warning-outline" size={28} color={colors.accent.red} />
              </View>
              <Text style={[styles.warningTitle, { color: colors.text.primary }]}>Delete Account</Text>
              <Text style={[styles.warning, { color: colors.text.tertiary }]}>{strings.profile.deleteAccount.warning}</Text>
              <Text style={[styles.disclaimer, { color: colors.text.dim }]}>{strings.profile.deleteAccount.disclaimer}</Text>
            </GlassCard>
          </ScrollView>

          <View style={styles.footer}>
            <TouchableOpacity
              style={[styles.deleteButton, { backgroundColor: colors.accent.redBg }, isSubmitting && styles.buttonDisabled]}
              disabled={isSubmitting}
              onPress={confirmAndSubmit}
            >
              <Text style={[styles.deleteButtonText, { color: colors.accent.red }]}>
                {isSubmitting ? 'Submitting...' : strings.profile.deleteAccount.button}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </BaseResidentMobileScreen>
    </GradientBackground>
  );
};

export const DeleteAccountConfirmedScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const dispatch = useAppDispatch();
  const insets = useSafeAreaInsets();

  return (
    <GradientBackground>
      <StatusBar barStyle="light-content" />
      <View style={[styles.confirmedContainer, { paddingBottom: insets.bottom + 24 }]}>
        <View style={styles.confirmedContent}>
          <View style={styles.checkCircle}>
            <Icon name="checkmark-circle" size={48} color={colors.accent.green} />
          </View>
          <Text style={[styles.confirmedTitle, { color: colors.text.primary }]}>{strings.profile.deleteConfirm.title}</Text>
          <Text style={[styles.confirmedBody, { color: colors.text.muted }]}>{strings.profile.deleteConfirm.message}</Text>
          <Text style={[styles.confirmedBody, { color: colors.text.muted }]}>{strings.profile.deleteConfirm.thanks}</Text>
        </View>
        <TouchableOpacity
          style={styles.logoutButton}
          onPress={() => { dispatch(logout()); }}
        >
          <Text style={styles.logoutButtonText}>{strings.profile.deleteConfirm.button}</Text>
        </TouchableOpacity>
      </View>
    </GradientBackground>
  );
};

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  container: {
    flex: 1,
    paddingHorizontal: HP,
  },
  scrollContent: {
    paddingTop: 8,
    gap: 16,
    flexGrow: 1,
  },
  warningCard: {
    alignItems: 'center',
    gap: 12,
  },
  warningIconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  warningTitle: {
    ...REDESIGN_TYPOGRAPHY.heading1,
    fontSize: 22,
    textAlign: 'center',
  },
  warning: {
    ...REDESIGN_TYPOGRAPHY.body,
    textAlign: 'center',
    lineHeight: 22,
  },
  disclaimer: {
    ...REDESIGN_TYPOGRAPHY.small,
    fontWeight: '400',
    textAlign: 'center',
    lineHeight: 18,
  },
  footer: {
    paddingTop: 16,
  },
  deleteButton: {
    paddingVertical: 14,
    alignItems: 'center',
    borderRadius: REDESIGN_SPACING.buttonRadius,
    borderWidth: 1,
    borderColor: 'rgba(245,85,96,0.3)',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  deleteButtonText: {
    fontWeight: '600',
    fontSize: 14,
  },
  confirmedContainer: {
    flex: 1,
    paddingHorizontal: HP,
    justifyContent: 'center',
  },
  confirmedContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  checkCircle: {
    marginBottom: 8,
  },
  confirmedTitle: {
    ...REDESIGN_TYPOGRAPHY.heading1,
    fontSize: 22,
    textAlign: 'center',
  },
  confirmedBody: {
    ...REDESIGN_TYPOGRAPHY.body,
    textAlign: 'center',
    lineHeight: 20,
  },
  logoutButton: {
    paddingVertical: 14,
    alignItems: 'center',
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
    borderRadius: REDESIGN_SPACING.buttonRadius,
  },
  logoutButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: 14,
  },
});

export default DeleteAccountScreen;
